import { useTranslation } from "react-i18next";

function clampProgress(progress: number): number {
  return Number.isNaN(progress) ? 0 : Math.max(0, Math.min(100, progress));
}

/** Progresso total da central: uma barra só, com a etapa corrente ao lado. */
export function OverallProgress({
  progress,
  stepLabel,
  description,
}: {
  progress: number;
  /** Texto visível ao lado do valor, ex.: "Step 2 of 3 · Management". */
  stepLabel: string;
  /** Frase detalhada, lida por leitor de tela junto do valor. */
  description: string;
}) {
  const { t } = useTranslation();
  const value = clampProgress(progress);

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--exed-ink-500)]">
            {t("Overall progress")}
          </span>
          <span className="text-[56px] font-bold leading-none tracking-[-0.03em] tabular-nums text-[var(--exed-ink-900)]">
            {value}%
          </span>
        </div>
        <span className="flex min-w-0 items-center gap-2 rounded-full bg-[rgba(255,200,61,0.14)] px-3 py-1.5">
          <span
            data-testid="overall-step-dot"
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full bg-[var(--exed-gold)] animate-cs-pulse motion-reduce:animate-none"
          />
          <span className="break-words font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7a5600]">
            {stepLabel}
          </span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={t("Overall progress")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value}
        aria-valuetext={`${value}% — ${description}`}
        className="h-3.5 overflow-hidden rounded-full bg-[#eef1f5]"
      >
        <div
          className="relative h-full overflow-hidden rounded-full bg-[linear-gradient(90deg,#08254c_0%,#1d4f91_65%,#ffc83d_100%)] shadow-[0_0_12px_rgba(255,200,61,0.35)] transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${value}%` }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.45)_50%,transparent_100%)] animate-cs-shimmer motion-reduce:hidden"
          />
        </div>
      </div>
    </div>
  );
}
